/**
 * Sign Up page
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export const SignUp: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signUp, confirmSignUp } = useAuth();
  const navigate = useNavigate();

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signUp(email, password, name);
      setNeedsConfirmation(true);
    } catch (err: any) {
      // Check for specific error types
      const errorMessage = err.message || err.toString() || 'Failed to sign up';

      if (errorMessage.includes('UsernameExistsException') ||
          errorMessage.includes('An account with the given email already exists') ||
          errorMessage.includes('User already exists')) {
        setError('An account with this email already exists. Please sign in instead.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await confirmSignUp(email, confirmationCode);
      navigate('/signin');
    } catch (err: any) {
      setError(err.message || 'Failed to confirm sign up');
    } finally {
      setLoading(false);
    }
  };

  if (needsConfirmation) {
    return (
      <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 md:px-6 py-8 md:py-12">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="mb-8 md:mb-12 text-center">
            <h1 className="text-3xl md:text-4xl font-bold mb-3">Confirm Your Email</h1>
            <p className="text-text-secondary font-mono text-xs md:text-sm">
              We've sent a confirmation code to <span className="text-white break-all">{email}</span>
            </p>
          </div>

          {/* Form Card */}
          <div className="card">
            <form onSubmit={handleConfirm} className="space-y-6">
              {/* Confirmation Code Field */}
              <div>
                <label className="block text-xs font-mono text-text-muted mb-2">
                  CONFIRMATION CODE *
                </label>
                <input
                  type="text"
                  value={confirmationCode}
                  onChange={(e) => setConfirmationCode(e.target.value)}
                  required
                  className="input font-mono"
                  placeholder="123456"
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="p-3 bg-red-950/20 border border-red-900/50 rounded text-red-400 text-sm">
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full"
              >
                {loading ? 'Confirming...' : 'Confirm Email'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 md:px-6 py-8 md:py-12">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="mb-8 md:mb-12 text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-3">Sign Up</h1>
          <p className="text-text-secondary font-mono text-xs md:text-sm">
            Start generating permitting matrices
          </p>
        </div>

        {/* Form Card */}
        <div className="card">
          <form onSubmit={handleSignUp} className="space-y-6">
            {/* Name Field */}
            <div>
              <label className="block text-xs font-mono text-text-muted mb-2">
                NAME
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input"
                placeholder="John Doe"
              />
            </div>

            {/* Email Field */}
            <div>
              <label className="block text-xs font-mono text-text-muted mb-2">
                EMAIL *
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="input"
                placeholder="you@company.com"
              />
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-xs font-mono text-text-muted mb-2">
                PASSWORD *
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="input"
                placeholder="••••••••"
              />
              <p className="text-xs text-text-muted mt-2 font-mono">
                Min 8 chars, uppercase, lowercase, number, special character
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-950/20 border border-red-900/50 rounded text-red-400 text-sm">
                {error}
                {error.includes('already exists') && (
                  <div className="mt-2">
                    <Link to="/signin" className="text-white hover:underline font-medium">
                      Go to Sign In →
                    </Link>
                  </div>
                )}
              </div>
            )}

            {/* Terms Agreement */}
            <p className="text-xs text-text-muted font-mono">
              By signing up, you agree to our{' '}
              <a href="/terms" className="text-white hover:underline">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacy" className="text-white hover:underline">
                Privacy Policy
              </a>
            </p>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
            >
              {loading ? 'Creating account...' : 'Sign Up'}
            </button>
          </form>
        </div>

        {/* Sign In Link */}
        <p className="mt-6 text-center text-sm text-text-secondary">
          Already have an account?{' '}
          <Link to="/signin" className="text-white hover:underline font-medium">
            Sign In
          </Link>
        </p>
      </div>
    </div>
  );
};
